import { useEffect } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { ROUTES } from '@/constants/routes.constants';
import { ROLES, STAFF_ROLES } from '@/constants/roles.constants';
import { GuestRoute } from '@/guards/GuestRoute';
import { ProtectedRoute } from '@/guards/ProtectedRoute';
import { RequirePermission } from '@/guards/RequirePermission';
import { AuthLayout } from '@/layouts/AuthLayout';
import { CheckinLayout } from '@/layouts/CheckinLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { ErrorLayout } from '@/layouts/ErrorLayout';
import { NotFoundPage } from '@/pages/error/NotFoundPage';
import { UnauthorizedPage } from '@/pages/error/UnauthorizedPage';
import { AuthPageLoader } from '@/components/feedback/AuthPageLoader';
import { SuspenseBoundary } from './SuspenseBoundary';
import { CHUNK_RELOAD_STORAGE_KEY, RouteErrorBoundary } from './RouteErrorBoundary';
import {
  CancellationsReportPage,
  ChangePasswordPage,
  CheckinPage,
  ChildCategoryListPage,
  ClientConfigurationPage,
  ClientListPage,
  ConfigurationListPage,
  DashboardPage,
  ForgotPasswordPage,
  GuestListPage,
  LoginPage,
  OccupancyReportPage,
  ProfilePage,
  RateListPage,
  RecaptchaSettingsPage,
  ReservationCalendarPage,
  ReservationFormPage,
  ReservationListPage,
  ReservationLogListPage,
  ResetPasswordPage,
  RestaurantHoursPage,
  RestaurantListPage,
  RestaurantTablesPage,
  SmtpSettingsPage,
  TemplateListPage,
  TopGuestsReportPage,
  UserListPage,
  VerifyEmailPage,
} from './lazy-imports';

const router = createBrowserRouter([
  {
    // Root errorElement — catches thrown render errors and stale dynamic-import chunk failures
    // from anywhere in the tree that doesn't handle its own error, instead of falling through to
    // React Router's bare-bones default error screen.
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <GuestRoute />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: ROUTES.LOGIN,
                element: (
                  <SuspenseBoundary fallback={<AuthPageLoader />}>
                    <LoginPage />
                  </SuspenseBoundary>
                ),
              },
              {
                path: ROUTES.FORGOT_PASSWORD,
                element: (
                  <SuspenseBoundary fallback={<AuthPageLoader />}>
                    <ForgotPasswordPage />
                  </SuspenseBoundary>
                ),
              },
              {
                path: ROUTES.RESET_PASSWORD,
                element: (
                  <SuspenseBoundary fallback={<AuthPageLoader />}>
                    <ResetPasswordPage />
                  </SuspenseBoundary>
                ),
              },
            ],
          },
        ],
      },
      {
        element: <AuthLayout />,
        children: [
          {
            // Reachable whether the user is signed in or not — links arrive via email.
            path: ROUTES.VERIFY_EMAIL,
            element: (
              <SuspenseBoundary fallback={<AuthPageLoader />}>
                <VerifyEmailPage />
              </SuspenseBoundary>
            ),
          },
        ],
      },
      {
        // Public, unauthenticated self-check-in — reachable whether the user is signed in or
        // not (a staff member's own device could scan a table's QR code too). Not behind
        // GuestRoute/ProtectedRoute: it must work with no session at all.
        element: <CheckinLayout />,
        children: [
          {
            path: ROUTES.CHECKIN,
            element: (
              <SuspenseBoundary fallback={<AuthPageLoader />}>
                <CheckinPage />
              </SuspenseBoundary>
            ),
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              {
                path: ROUTES.DASHBOARD,
                handle: { breadcrumb: 'nav.dashboard' },
                element: (
                  <SuspenseBoundary>
                    <DashboardPage />
                  </SuspenseBoundary>
                ),
              },
              {
                path: ROUTES.PROFILE,
                handle: { breadcrumb: 'account.profile' },
                element: (
                  <SuspenseBoundary>
                    <ProfilePage />
                  </SuspenseBoundary>
                ),
              },
              {
                path: ROUTES.CHANGE_PASSWORD,
                handle: { breadcrumb: 'account.changePassword' },
                element: (
                  <SuspenseBoundary>
                    <ChangePasswordPage />
                  </SuspenseBoundary>
                ),
              },
              {
                // Client (tenant) management and Settings > Template Management / SMTP / reCAPTCHA
                // are exclusively an Admin capability — mirrors the same gating applied to their
                // sidebar nav entries in navigation.config.ts. These settings are platform-wide,
                // not per-tenant, so they sit alongside Clients here rather than under the
                // STAFF_ROLES block.
                element: <RequirePermission roles={[ROLES.ADMIN]} />,
                children: [
                  {
                    path: ROUTES.CLIENTS,
                    handle: { breadcrumb: 'nav.clients' },
                    element: (
                      <SuspenseBoundary>
                        <ClientListPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.CLIENT_CONFIGURATION,
                    handle: { breadcrumb: ['nav.clients', 'clients.configuration.pageTitle'] },
                    element: (
                      <SuspenseBoundary>
                        <ClientConfigurationPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.SETTINGS_TEMPLATES,
                    handle: { breadcrumb: ['nav.settings', 'nav.settingsTemplates'] },
                    element: (
                      <SuspenseBoundary>
                        <TemplateListPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.SETTINGS_SMTP,
                    handle: { breadcrumb: ['nav.settings', 'nav.settingsSmtp'] },
                    element: (
                      <SuspenseBoundary>
                        <SmtpSettingsPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.SETTINGS_RECAPTCHA,
                    handle: { breadcrumb: ['nav.settings', 'nav.settingsRecaptcha'] },
                    element: (
                      <SuspenseBoundary>
                        <RecaptchaSettingsPage />
                      </SuspenseBoundary>
                    ),
                  },
                ],
              },
              {
                // Every operational module below is off-limits to the Admin role, which is
                // restricted to the Dashboard and Clients only — mirrors the same gating
                // applied to these sidebar nav entries in navigation.config.ts.
                element: <RequirePermission roles={STAFF_ROLES} />,
                children: [
                  {
                    path: ROUTES.RESERVATIONS,
                    handle: { breadcrumb: 'nav.reservations' },
                    element: (
                      <SuspenseBoundary>
                        <ReservationListPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.RESERVATIONS_CREATE,
                    handle: { breadcrumb: ['nav.reservations', 'reservations.createReservation'] },
                    element: (
                      <SuspenseBoundary>
                        <ReservationFormPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.RESERVATIONS_EDIT,
                    handle: { breadcrumb: ['nav.reservations', 'common.edit'] },
                    element: (
                      <SuspenseBoundary>
                        <ReservationFormPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.RESERVATIONS_CALENDAR,
                    handle: {
                      breadcrumb: ['nav.reservations', 'reservations.calendarView.pageTitle'],
                    },
                    element: (
                      <SuspenseBoundary>
                        <ReservationCalendarPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.GUESTS,
                    handle: { breadcrumb: 'nav.guests' },
                    element: (
                      <SuspenseBoundary>
                        <GuestListPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.RESTAURANTS,
                    handle: { breadcrumb: 'nav.restaurants' },
                    element: (
                      <SuspenseBoundary>
                        <RestaurantListPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.RESTAURANT_HOURS,
                    handle: { breadcrumb: ['nav.restaurants', 'restaurantHours.pageTitle'] },
                    element: (
                      <SuspenseBoundary>
                        <RestaurantHoursPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.RESTAURANT_TABLES,
                    handle: { breadcrumb: ['nav.restaurants', 'restaurantTables.pageTitle'] },
                    element: (
                      <SuspenseBoundary>
                        <RestaurantTablesPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.SETTINGS_RATES,
                    handle: { breadcrumb: ['nav.settings', 'nav.settingsRates'] },
                    element: (
                      <SuspenseBoundary>
                        <RateListPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.SETTINGS_CHILD_CATEGORIES,
                    handle: { breadcrumb: ['nav.settings', 'nav.settingsChildCategories'] },
                    element: (
                      <SuspenseBoundary>
                        <ChildCategoryListPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.SETTINGS_CONFIGURATION,
                    handle: { breadcrumb: ['nav.settings', 'nav.settingsConfiguration'] },
                    element: (
                      <SuspenseBoundary>
                        <ConfigurationListPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.USERS,
                    handle: { breadcrumb: 'nav.users' },
                    element: (
                      <SuspenseBoundary>
                        <UserListPage />
                      </SuspenseBoundary>
                    ),
                  },
                ],
              },
              {
                // Logs is restricted to Owner — mirrors the same gating applied to
                // its sidebar nav entry in navigation.config.ts.
                element: <RequirePermission roles={[ROLES.OWNER]} />,
                children: [
                  {
                    path: ROUTES.LOGS,
                    handle: { breadcrumb: ['nav.logs', 'nav.logsList'] },
                    element: (
                      <SuspenseBoundary>
                        <ReservationLogListPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.LOGS_OCCUPANCY,
                    handle: { breadcrumb: ['nav.logs', 'nav.logsOccupancy'] },
                    element: (
                      <SuspenseBoundary>
                        <OccupancyReportPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.LOGS_TOP_GUESTS,
                    handle: { breadcrumb: ['nav.logs', 'nav.logsTopGuests'] },
                    element: (
                      <SuspenseBoundary>
                        <TopGuestsReportPage />
                      </SuspenseBoundary>
                    ),
                  },
                  {
                    path: ROUTES.LOGS_CANCELLATIONS,
                    handle: { breadcrumb: ['nav.logs', 'nav.logsCancellations'] },
                    element: (
                      <SuspenseBoundary>
                        <CancellationsReportPage />
                      </SuspenseBoundary>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: ROUTES.ROOT,
        element: <Navigate to={ROUTES.DASHBOARD} replace />,
      },
      {
        element: <ErrorLayout />,
        children: [
          { path: ROUTES.UNAUTHORIZED, element: <UnauthorizedPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

export function AppRoutes() {
  // A route tree that renders cleanly means we're past whatever the last stale-chunk reload
  // was for — clear the guard so a future redeploy gets its own fresh reload attempt.
  useEffect(() => {
    sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
  }, []);

  return <RouterProvider router={router} />;
}
